import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { dashboardApi } from '@/services/api';
import { formatDate, utcToKolkataParts, formatDateTime } from '@/utils/dateTime';
import {
  Users,
  Calendar,
  Stethoscope,
  UserCheck,
  Clock,
  Activity,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, formatDoctorName, getDoctorInitials } from '@/lib/utils';

const StatCard = ({ title, value, icon: Icon, description, trend, color = 'teal' }) => {
  const colorClasses = {
    teal: 'bg-teal-50 text-teal-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                <span className="text-sm text-emerald-600">{trend}</span>
              </div>
            )}
          </div>
          <div className={cn('p-3 rounded-xl', colorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AppointmentRow = ({ appointment, showDoctor = true, showPatient = true }) => {
  const navigate = useNavigate();
  const { buildUrl } = useClinic();
  
  const statusStyles = {
    booked: 'status-booked',
    completed: 'status-completed',
    cancelled: 'status-cancelled',
    pending: 'status-pending',
    CONFIRMED: 'status-booked',
    COMPLETED: 'status-completed',
    CANCELLED: 'status-cancelled',
    PENDING: 'status-pending',
  };

  const patient = appointment.patient || {};
  const patientDisplay = patient.name || appointment.patientName || `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Patient';
  const pParts = patientDisplay.split(' ').filter(Boolean);
  const patientFirst = patient.firstName || pParts[0] || 'Patient';
  const patientLast = patient.lastName || (pParts.length > 1 ? pParts.slice(1).join(' ') : '');

  const doctor = appointment.doctor || {};
  const doctorDisplay = formatDoctorName(doctor.name || doctor.firstName ? doctor : (appointment.doctorName || doctor));

  const timeDisplay = appointment.timeFormatted || appointment.time || (appointment.startTimeUtc ? formatDateTime(appointment.startTimeUtc) : '09:00 AM');
  const typeDisplay = String(appointment.type || appointment.appointmentType || appointment.reason || 'Consultation').replace(/[-_]/g, ' ');

  return (
    <div 
      className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
      onClick={() => navigate(buildUrl(`appointments/${appointment.id || ''}`))}
      data-testid={`appointment-row-${appointment.id || 'new'}`}
    >
      <div className="flex items-center gap-4">
        {showPatient && (
          <Avatar className="h-10 w-10">
            <AvatarImage src={patient.avatar} />
            <AvatarFallback className="bg-slate-100 text-slate-600 text-sm">
              {patientFirst[0] || 'P'}{patientLast[0] || ''}
            </AvatarFallback>
          </Avatar>
        )}
        <div>
          {showPatient && (
            <p className="font-medium text-slate-900">
              {patientDisplay}
            </p>
          )}
          {showDoctor && (
            <p className="text-sm text-slate-500">
              {doctorDisplay}
            </p>
          )}
          <p className="text-sm text-slate-500">
            {timeDisplay} • {typeDisplay}
          </p>
        </div>
      </div>
      <Badge variant="outline" className={statusStyles[appointment.status] || 'bg-slate-100 text-slate-700'}>
        {appointment.status || 'SCHEDULED'}
      </Badge>
    </div>
  );
};

const DashboardPage = () => {
  const { user, hasPermission } = useAuth();
  const { buildUrl, activeClinicId, hospitalId } = useClinic();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeClinicId, hospitalId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getStats(activeClinicId, hospitalId);
      setStats(data || {});
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setStats({});
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const canViewAllAppointments = hasPermission('APPOINTMENT_ALL_VIEW') || hasPermission('PLATFORM_MANAGE');
  const commonRole = (user?.commonRole || user?.designation || user?.role || '').toLowerCase();
  const displayName = user?.firstName || user?.username || 'User';

  // If user does not have permission to view all appointments or is designated as doctor, show personal schedule view
  const isPersonalScheduleView = !canViewAllAppointments && (commonRole.includes('doctor') || hasPermission('CONSULTATION_WRITE'));

  if (isPersonalScheduleView) {
    return (
      <div className="space-y-6" data-testid="doctor-dashboard">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              Good {utcToKolkataParts(new Date()).hour24 < 12 ? 'morning' : 'afternoon'}, {commonRole.includes('doctor') ? formatDoctorName(user, displayName) : displayName}
            </h1>
            <p className="text-slate-500">
              Here's your schedule for today
            </p>
          </div>
          <p className="text-sm text-slate-500">
            {formatDate(new Date(), 'FULL')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Today's Appointments"
            value={stats?.todaysAppointments || stats?.appointments?.length || 0}
            icon={Calendar}
            color="teal"
          />
          <StatCard
            title="Pending Consultations"
            value={stats?.pendingConsultations || 0}
            icon={Clock}
            color="amber"
          />
          <StatCard
            title="Completed Today"
            value={stats?.completedToday || 0}
            icon={Stethoscope}
            color="emerald"
          />
          <StatCard
            title="Follow-ups Due"
            value={stats?.followUps || 0}
            icon={AlertCircle}
            color="blue"
          />
        </div>

        {/* Today's Schedule */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg">Today's Schedule</CardTitle>
              <CardDescription>Your appointments for today</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(buildUrl('appointments'))}
              data-testid="view-my-schedule"
            >
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {Array.isArray(stats?.appointments) && stats.appointments.length > 0 ? (
              <div className="space-y-2">
                {stats.appointments.map(apt => (
                  <AppointmentRow key={apt.id || Math.random()} appointment={apt} showDoctor={false} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No appointments scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Front Desk / Receptionist View (has OPD_QUEUE_MANAGE / PATIENT_WRITE but not full ADMIN management)
  const isFrontDeskView = commonRole.includes('receptionist') || (hasPermission('OPD_QUEUE_MANAGE') && !hasPermission('STAFF_MANAGE'));
  if (isFrontDeskView) {
    return (
      <div className="space-y-6" data-testid="receptionist-dashboard">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              Welcome, {displayName}
            </h1>
            <p className="text-slate-500">
              Front desk overview for today
            </p>
          </div>
          <p className="text-sm text-slate-500">
            {formatDate(new Date(), 'FULL')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={() => navigate(buildUrl('patients/new'))}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="h-4 w-4 mr-2" /> Register New Patient
          </Button>
          <Button 
            onClick={() => navigate(buildUrl('appointments/new'))}
            variant="outline"
          >
            <Calendar className="h-4 w-4 mr-2" /> Book Appointment
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Today's Bookings"
            value={stats?.todaysBookings || stats?.appointments?.length || 0}
            icon={Calendar}
            color="teal"
          />
          <StatCard
            title="Patients Registered Today"
            value={stats?.registeredToday || 0}
            icon={Users}
            color="emerald"
          />
          <StatCard
            title="Pending Check-ins"
            value={stats?.pendingCheckIns || 0}
            icon={Clock}
            color="blue"
          />
        </div>

        {/* Appointments List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg">Today's Appointments</CardTitle>
              <CardDescription>All scheduled appointments</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(buildUrl('appointments'))}
              data-testid="view-all-bookings"
            >
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {Array.isArray(stats?.appointments) && stats.appointments.length > 0 ? (
              <div className="space-y-2">
                {stats.appointments.map(apt => (
                  <AppointmentRow key={apt.id || Math.random()} appointment={apt} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No appointments scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin / Hospital Admin / Default Dashboard View
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            Welcome back, {displayName}
          </h1>
          <p className="text-slate-500">
            Here's what's happening at your hospital today
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {formatDate(new Date(), 'FULL')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={stats?.totalPatients || 0}
          icon={Users}
          trend={stats?.patientTrend}
          color="teal"
        />
        <StatCard
          title="Consultations"
          value={stats?.totalConsultationsToday || 0}
          icon={Stethoscope}
          color="emerald"
        />
        <StatCard
          title="Pending Appointments"
          value={stats?.pendingAppointments || 0}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Active Doctors"
          value={stats?.totalDoctors || 0}
          icon={UserCheck}
          color="blue"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Appointments */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg">Today's Appointments</CardTitle>
              <CardDescription>Recent appointments and their status</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(buildUrl('appointments'))}
              data-testid="view-all-appointments"
            >
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {Array.isArray(stats?.recentAppointments) && stats.recentAppointments.length > 0 ? (
              <div className="space-y-2">
                {stats.recentAppointments.map(apt => (
                  <AppointmentRow key={apt.id || Math.random()} appointment={apt} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No appointments scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Doctor Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Doctor Activity</CardTitle>
            <CardDescription>Today's performance</CardDescription>
          </CardHeader>
          <CardContent>
            {Array.isArray(stats?.doctorActivity) && stats.doctorActivity.length > 0 ? (
              <div className="space-y-4">
                {stats.doctorActivity.slice(0, 5).map((item, idx) => {
                  const doc = item.doctor || {};
                  const doctorTarget = doc.name || doc.firstName ? doc : (item.doctorName || 'Doctor');
                  const doctorDisplay = formatDoctorName(doctorTarget);
                  const doctorInitials = getDoctorInitials(doctorTarget);
                  return (
                    <div key={doc.id || item.doctorId || idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={doc.avatar} />
                          <AvatarFallback className="bg-teal-100 text-teal-700 text-xs">
                            {doctorInitials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {doctorDisplay}
                          </p>
                          <p className="text-xs text-slate-500">
                            {doc.specialization || item.department || 'Outpatient Unit'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900">
                          {item.completedToday ?? 0}/{item.appointmentsToday ?? 0}
                        </p>
                        <p className="text-xs text-slate-500">completed</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Activity className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No doctor activity today</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
